/**
 * ShuffleDebugOverlay — admin-only floating panel (bottom-right).
 * Visible only when a Supabase session is active (i.e. admin is logged in).
 * Shows the current shuffle queue order and the persisted played-history
 * from localStorage so you can visually inspect anti-clustering logic.
 *
 * Collapse/expand with the ▲ / ▼ button. Zero impact on production visitors.
 */
import { useState, useEffect, useCallback, useLayoutEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import type { QueueTrack } from "@/hooks/usePlaybackEngine";

const HISTORY_KEY   = "llf_played_history";
const OPEN_STATE_KEY = "llf_debug_open";

function readHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Returns persisted open state, or null if never saved (fall back to mobile default). */
function readOpenState(): boolean | null {
  try {
    const v = localStorage.getItem(OPEN_STATE_KEY);
    if (v === null) return null;
    return v === "1";
  } catch {
    return null;
  }
}

function saveOpenState(v: boolean) {
  try { localStorage.setItem(OPEN_STATE_KEY, v ? "1" : "0"); } catch { /* ignore */ }
}

interface Props {
  queue: QueueTrack[];
  currentId: string | null;
  /** Increments whenever the queue is rebuilt so we can re-read history */
  queueVersion: number;
}

export default function ShuffleDebugOverlay({ queue, currentId, queueVersion }: Props) {
  const isMobile = useIsMobile();
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState<boolean | null>(null); // null = not yet initialised
  const [history, setHistory] = useState<string[]>([]);

  // Initialise open state: use persisted value if available, otherwise mobile-aware default
  useLayoutEffect(() => {
    const persisted = readOpenState();
    setOpen(persisted !== null ? persisted : !isMobile);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Only render for authenticated admins
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setIsAdmin(!!s);
    });
    supabase.auth.getSession().then(({ data }) => setIsAdmin(!!data.session));
    return () => subscription.unsubscribe();
  }, []);

  // Refresh played history whenever the queue rebuilds or track changes
  useEffect(() => {
    setHistory(readHistory());
  }, [queueVersion, currentId]);

  const refresh = useCallback(() => setHistory(readHistory()), []);

  const clearHistory = useCallback(() => {
    try { localStorage.removeItem(HISTORY_KEY); } catch { /* ignore */ }
    setHistory([]);
  }, []);

  if (!isAdmin) return null;
  // Don't render content until open state is initialised from mobile detection
  if (open === null) return null;

  const historySet = new Set(history);

  return (
    <div
      style={{ zIndex: 1000, maxWidth: "280px" }}
      className="fixed bottom-[10px] left-[5%] w-[90%] max-[480px]:bottom-[5px] max-[480px]:left-[2.5%] max-[480px]:w-[95%] max-h-[60vh] flex flex-col rounded-md border border-white/10 bg-black/85 backdrop-blur-md text-[9px] font-mono shadow-md overflow-hidden"
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-white/70 uppercase tracking-widest text-[9px]">
            Shuffle Debug
          </span>
          <span className="text-white/30">·</span>
          <span className="text-white/50">{queue.length} tracks</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            title="Refresh history"
            className="text-white/40 hover:text-white/80 transition-colors px-1"
          >
            ↺
          </button>
          <button
            onClick={() => setOpen((v) => { const next = !v; saveOpenState(next); return next; })}
            className="text-white/40 hover:text-white/80 transition-colors px-1"
          >
            {open ? "▼" : "▲"}
          </button>
        </div>
      </div>

      {open && (
        <div className="flex flex-col overflow-hidden">
          {/* Played history section */}
          <div className="px-3 pt-2 pb-1 border-b border-white/10 shrink-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-yellow-400/80 uppercase tracking-widest text-[9px]">
                Played History ({history.length}/{queue.length})
              </span>
              {history.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="text-red-400/60 hover:text-red-400 transition-colors text-[9px] uppercase tracking-widest"
                >
                  Clear
                </button>
              )}
            </div>
            {history.length === 0 ? (
              <p className="text-white/20 italic py-1">No history yet.</p>
            ) : (
              <ol className="flex flex-col gap-0.5">
                {[...history].reverse().map((id, i) => {
                  const track = queue.find((t) => t.id === id);
                  return (
                    <li key={id} className="flex items-center gap-1.5">
                      <span className="text-white/20 w-4 text-right shrink-0">{history.length - i}.</span>
                      <span className={`truncate ${id === currentId ? "text-green-400" : "text-white/50"}`}>
                        {track ? `${track.title} — ${track.author}` : id}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {/* Queue order section */}
          <div className="px-3 pt-2 pb-3 overflow-y-auto">
            <div className="mb-1">
              <span className="text-blue-400/80 uppercase tracking-widest text-[9px]">
                Queue Order
              </span>
            </div>
            <ol className="flex flex-col gap-0.5">
              {queue.map((track, idx) => {
                const isCurrent = track.id === currentId;
                const isStale = historySet.has(track.id);
                return (
                  <li key={track.id} className="flex items-start gap-1.5">
                    <span className="text-white/20 w-5 text-right shrink-0 pt-px">{idx + 1}.</span>
                    <div className="flex flex-col min-w-0">
                      <span
                        className={`truncate leading-tight ${
                          isCurrent
                            ? "text-green-400 font-bold"
                            : isStale
                            ? "text-yellow-400/50"
                            : "text-white/60"
                        }`}
                      >
                        {track.title}
                      </span>
                      <span className="text-white/25 truncate leading-tight">
                        {track.author} · A{track.albumId}
                        {track.isIntro && (
                          <span className="ml-1 text-purple-400/70">[intro]</span>
                        )}
                        {isStale && (
                          <span className="ml-1 text-yellow-400/50">[heard]</span>
                        )}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Legend */}
          <div className="px-3 py-2 border-t border-white/10 flex gap-3 shrink-0">
            <span className="text-green-400/70">▪ now playing</span>
            <span className="text-yellow-400/50">▪ heard recently</span>
            <span className="text-purple-400/70">▪ intro</span>
          </div>
        </div>
      )}
    </div>
  );
}
